/**
 * Page titles and descriptions, in one place.
 *
 * The tab title, the canonical link and the social preview all follow the route,
 * so a shared quote link or an admin bookmark reads properly instead of showing
 * the same generic title everywhere.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { trackPageView as reportPageView } from "@/lib/analytics";

const SITE_NAME = "T-Tite Calculator";
const CANONICAL_ORIGIN = "https://calc.t-tite.co.za";

const DEFAULT_DESCRIPTION =
  "Get an instant, itemised quote for audits, independent reviews, financial statements and monthly bookkeeping from T-TITE Registered Accountants and Auditors.";

export const PAGE_META = {
  "/": {
    title: SITE_NAME,
    description: DEFAULT_DESCRIPTION,
  },
  "/pricing-calculator": {
    title: `Get a quote · ${SITE_NAME}`,
    description:
      "Answer a few questions about your business and get an instant quote for an audit, an independent review, financial statements or monthly bookkeeping.",
  },
  "/bookkeeping-fees": {
    title: "Bookkeeping Fees | Get an Instant Estimate | T-Tite",
    description:
      "Estimate your monthly bookkeeping fees online. Get an indicative quote in about 2 minutes from T-Tite.",
  },
  "/financial-statement-fees": {
    title: "Financial Statement Fees | Get an Estimate | T-Tite",
    description:
      "Estimate the cost of preparing your annual financial statements. Get an indicative T-Tite fee estimate online.",
  },
  "/audit-review-fees": {
    title: "Audit & Independent Review Fees | T-Tite",
    description:
      "Get an initial fee estimate for an audit or independent review from T-Tite's professional assurance team.",
  },
  "/outsourced-financial-management": {
    title: "Outsourced Financial Management | T-Tite",
    description:
      "An outsourced finance function led by a Financial Manager - reporting, cash flow, compliance and decision support, scoped around your business.",
  },
  "/verify-quote": {
    title: `Verify a quote · ${SITE_NAME}`,
    description:
      "Check that a T-TITE quote reference is genuine and see the figure we issued.",
  },
  "/admin/login": {
    title: `Team sign in · ${SITE_NAME}`,
    description: "T-TITE team sign in.",
    noIndex: true,
  },
  "/admin": {
    title: `Dashboard · ${SITE_NAME}`,
    description: "T-TITE quotes, leads and rate card.",
    noIndex: true,
  },
};

// The snippet in index.html already counts the page the visitor landed on, so we
// start from that path and only report genuine changes. Module scope (rather
// than a ref) keeps it honest under React's double-invoked effects in dev.
let lastTrackedPath =
  typeof window === "undefined" ? null : window.location.pathname;

const trackPageView = (pathname) => {
  if (pathname === lastTrackedPath) return;
  lastTrackedPath = pathname;
  reportPageView();
};

const setMeta = (selector, attribute, value) => {
  const tag = document.head.querySelector(selector);
  if (tag) tag.setAttribute(attribute, value);
};

/** Keeps the tab title and SEO tags in step with the current route. */
export const usePageMeta = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = PAGE_META[pathname] || PAGE_META["/"];
    const canonical = `${CANONICAL_ORIGIN}${pathname === "/" ? "/" : pathname}`;

    document.title = meta.title;
    setMeta('meta[name="description"]', "content", meta.description);
    setMeta('meta[property="og:title"]', "content", meta.title);
    setMeta('meta[property="og:description"]', "content", meta.description);
    setMeta('meta[property="og:url"]', "content", canonical);
    setMeta('meta[name="twitter:title"]', "content", meta.title);
    setMeta('meta[name="twitter:description"]', "content", meta.description);
    setMeta('link[rel="canonical"]', "href", canonical);
    setMeta(
      'meta[name="robots"]',
      "content",
      meta.noIndex ? "noindex, nofollow" : "index, follow",
    );

    // This is a single-page app, so the Meta Pixel snippet in index.html only
    // fires on first load. Count the in-app page changes too, without
    // double-counting the page the visitor arrived on.
    trackPageView(pathname);
  }, [pathname]);
};
